import React, {useEffect} from "react";
import ReportUtils from "../../../../../utils/report-utils.js";
import ProjectUtils from "../../../../../utils/project-utils.js";
import {navigateHook} from "../../../../../hooks/use-navigate.js";

const DESPACHO_WEB_URL = import.meta.env.VITE_API_DESPACHO_WEB;

const formatPeriodDate = (dtStart, dtEnd, uncertain) => {
    if (dtStart && dtEnd){
        return `${ReportUtils.formatDateTimeLabel(dtStart, uncertain)} - ${ReportUtils.formatDateTime(dtEnd)}`
    } else {
        return ReportUtils.formatDateTimeLabel(dtStart, uncertain);
    }
}

const ReportContent = ({data, boName, tableColumns}) => {
    const sourceOptions = {APP: "App", UPLOAD_FILE: "Envio de arquivo", WEB: "Área web"};
    const navigate = navigateHook();
    const rows = data?.result ?? [];

    const getFactDate = (date, uncertain) => date ? ReportUtils.formatDateTimeLabel(date, uncertain) : "Não informado";

    const getCreatedBy = (element) => {
        const source = sourceOptions[element.source] ?? "";
        let createdBy = (element.system_user_namewar ?? "") + (source ? ` (${source})` : "");
        if (element.phone) createdBy += " - " + ProjectUtils.formatPhone(element.phone);
        return createdBy;
    };

    const getFullAddress = (element) => {
        const address = element.address ?? "";
        const number = element.number ?? "";
        const neighborhood = element.neighborhood ?? "";
        const place = element.place ?? "";

        return [
            place,
            address ? address + (number ? ` ${number}` : "") : "",
            neighborhood,
        ].filter(Boolean).reduce((term, value, index) => {
            if (index === 0) return value;
            if (index === 1) return term + ", " + value;
            return term + " - " + value;
        }, "");
    };

    const getDispatchLabel = (element) => {
        if (element?.numbercheckbook) return element.numbercheckbook;
        if (element?.dispatch_id) return "Sem talão";
        return "Não informado";
    };

    const hasValue = (value) => value && value !== "" ? value : "Não informado";

    const columns = [
        {
            id: "id",
            label: boName,
            className: "w-[5%]",
            getValue: element => element.id,
            render: element => {
                const url = `/bo/redirect/${element.id.replace("/", "-")}`;
                return <a onClick={() => navigate(url, true)} className="inner-link">{element.id}</a>;
            }
        },
        {id: "status", label: "Status", className: "w-[5%]", getValue: element => element.status, render: element => element.status},
        {
            id: "dt_create",
            label: "D/H cadastro",
            className: "w-[9%]",
            getValue: element => element.dt_create ?? element.date_time ?? "Não informado",
            render: element => element.dt_create ?? element.date_time ?? "Não informado"
        },
        {
            id: "dt_fact",
            label: "D/H fato",
            className: "w-[9%]",
            getValue: element => getFactDate(element.dt_fact, element.dt_fact_uncertain),
            render: element => hasValue(element.dt_fact ? formatPeriodDate(element.dt_fact, element.dt_end, element.dt_fact_uncertain) : "")
        },
        {
            id: "dt_end",
            exportGroup: "dt_fact",
            label: "D/H final do fato",
            getValue: element => element.dt_end ? ReportUtils.formatDateTime(element.dt_end) : "Não informado",
            render: element => element.dt_end ? ReportUtils.formatDateTime(element.dt_end) : "Não informado"
        },
        {id: "created_by", label: "Cadastrado por", className: "w-[9%]", getValue: getCreatedBy, render: element => hasValue(getCreatedBy(element))},
        {id: "leader", label: "Responsável", className: "w-[9%]", getValue: element => element.leader ?? "Não informado", render: element => hasValue(element.leader)},
        {id: "reason", label: "Natureza", className: "w-[8%]", getValue: element => element.reason ?? "Não informado", render: element => hasValue(element.reason)},
        {id: "flagrant", label: "Flagrante", className: "w-[5%]", getValue: element => element.flagrant ?? "Não informado", render: element => hasValue(element.flagrant)},
        {id: "address", label: "Endereço", className: "w-[11%]", getValue: element => getFullAddress(element) || "Não informado", render: element => hasValue(getFullAddress(element))},
        {
            id: "dispatch",
            label: "Despacho",
            className: "w-[5%]",
            getValue: getDispatchLabel,
            render: element => {
                const label = getDispatchLabel(element);
                const url = `/despacho/dispatch/form/${element?.dispatch_id}`;
                return element?.dispatch_id ? <a onClick={() => navigate(url, true)} className="inner-link">{label}</a> : label;
            }
        },
        {
            id: "garrison",
            label: "Guarnição",
            className: "w-[10%]",
            getValue: element => element.garrison_name ?? "",
            render: element => {
                const url = `/despacho/garrison/form/${element?.garrison_id}`;
                return element?.garrison_id ? <a onClick={() => navigate(url, true)} className="inner-link">{element.garrison_name}</a> : element.garrison_name ?? "Não informado";
            }
        },
        {id: "team", label: "Equipe", className: "w-[5%]", getValue: element => element.garrison_team ?? "Não informado", render: element => hasValue(element.garrison_team)},
        {id: "contingents", label: "Contingentes", className: "w-[10%]", getValue: element => element.contingent_namewar ?? "Não informado", render: element => hasValue(element.contingent_namewar)}
    ];

    const isExportOnly = (column) => tableColumns?.[column.id]?.exportOnly === true;
    const isColumnVisible = (column) => isExportOnly(column) || tableColumns?.[column.id]?.visible !== false;
    const isExportGroupVisible = (column) => !column.exportGroup || tableColumns?.[column.exportGroup]?.visible !== false;
    const screenColumns = columns.filter(column => !isExportOnly(column) && isColumnVisible(column));
    const exportColumns = columns.filter(column => isExportGroupVisible(column) && isColumnVisible(column));

    useEffect(() => {
        const labels = exportColumns.map(column => column.label);
        const values = rows.map(element => exportColumns.map(column => column.getValue(element)));

        ReportUtils.createExportableTable(labels, values, `Lista de ${boName}`);
        ReportUtils.withoutContentReadjust();
    }, [data, boName, tableColumns]);

    return (
        <>
            {screenColumns.length === 0 && <div id="noDataMessage" className="alert alert-info p-2 h6 text-center mt-[20px]!">Todas as colunas foram ocultadas.</div>}
            <table id="list-bos-data" className={`table report-table ${screenColumns.length === 0 ? "hidden" : ""}`}>
                <thead className="thead-background">
                    <tr className="bg-[#f6f6f6] text-left">
                        {screenColumns.map(column => <th key={column.id} className={column.className}>{column.label}</th>)}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((element, index) => (
                        <tr key={element.id ?? index} className="table-break-word">
                            {screenColumns.map(column => <td key={column.id} className={column.className}>{column.render(element)}</td>)}
                        </tr>
                    ))}
                </tbody>
            </table>

            <div id="chart-tables"/>
        </>
    );
};

export default ReportContent;